import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Home, RefreshCcw, LayoutGrid, Sun, Moon, X, BarChart3, FileText, History } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUIStore } from '@/stores';
import { useAuthStore } from '@/stores/useAuthStore';
import { LanguageSelector } from '@/shared/ui/LanguageSelector';

import { ProjectMeta, ProjectSnapshot } from '@/shared/model';
import type { Room } from '@/entities/room/model/types';
import type { EstimateItem } from '@/entities/estimate/model/types';
import type { BOMTotalsResult } from '@/entities/material';

// Subcomponents
import { ProjectStats } from './sidebar/ProjectStats';
import { ProjectMetaForm } from './sidebar/ProjectMetaForm';
import { VersionHistory } from './sidebar/VersionHistory';

type SidebarTab = 'stats' | 'project' | 'history';

interface EditorSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    projectTotal: number;
    totalArea: number;
    safetyMargin: number;
    setSafetyMargin: (margin: number) => void;
    onDownloadExcel: () => void;
    onSyncPrices: () => void;
    meta?: ProjectMeta;
    updateMeta?: (updates: Partial<ProjectMeta>) => void;
    activeProjectId?: string | number | null;
    commitVersion?: (name: string) => void;
    onLoadSnapshot?: (snapshot: ProjectSnapshot) => void;
    rooms?: Room[];
    projectItems?: EstimateItem[];
    /** CR-01 (MAT-04): pre-computed material totals, threaded to ProjectStats → ExportPDFButton. */
    materialTotals?: BOMTotalsResult;
    /** TRUTH-02: project-level geometry override, threaded to ProjectStats → ExportPDFButton. */
    projectOverrides?: { floorArea?: number; wallArea?: number; perimeter?: number };
}

export const EditorSidebar: FC<EditorSidebarProps> = ({
    isOpen,
    onClose,
    projectTotal,
    totalArea,
    safetyMargin,
    setSafetyMargin,
    onDownloadExcel,
    onSyncPrices,
    meta = {},
    updateMeta,
    activeProjectId,
    commitVersion,
    onLoadSnapshot,
    rooms = [],
    projectItems = [],
    materialTotals,
    projectOverrides,
}) => {
    const { t } = useTranslation();
    const theme = useUIStore((s) => s.theme);
    const toggleTheme = useUIStore((s) => s.toggleTheme);
    // Read-only preview показывает fixed-баннер сверху (ReadOnlyBanner, h-10). Сайдбар тоже
    // position:fixed, поэтому pt-10 у обёртки роутов на него не влияет — смещаем его вручную.
    const readOnlyPreview = useAuthStore((s) => s.readOnlyPreview);
    const [activeTab, setActiveTab] = useState<SidebarTab>('stats');

    const tabs: { id: SidebarTab; label: string; icon: typeof BarChart3 }[] = [
        { id: 'stats', label: t('editor.sidebar.tab_stats', 'Сводка'), icon: BarChart3 },
        { id: 'project', label: t('editor.sidebar.tab_project', 'Проект'), icon: FileText },
        { id: 'history', label: t('editor.sidebar.tab_history', 'История'), icon: History },
    ];

    return (
        <>
            <AnimatePresence>
                {isOpen && (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden backdrop-blur-sm"
                        onClick={onClose}
                    />
                )}
            </AnimatePresence>

            <motion.aside 
                initial={false}
                animate={{ 
                    x: isOpen ? 0 : -320
                }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                className={`
                    bg-white dark:bg-slate-900 flex flex-col z-50
                    fixed left-0 w-[320px] overflow-hidden
                    ${readOnlyPreview ? 'top-10 bottom-0' : 'inset-y-0'}
                    ${isOpen ? 'border-r border-slate-200 dark:border-slate-800 shadow-xl lg:shadow-none' : 'border-r-0 border-transparent'}
                `}
            >
                <div className="w-[320px] flex flex-col h-full flex-shrink-0">
                    {/* Header — compact */}
                    <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                            <div className="bg-blue-600 p-1.5 rounded-lg shadow-lg shadow-blue-200 dark:shadow-none">
                                <Home className="text-white" size={16} />
                            </div>
                            {/* eslint-disable-next-line i18next/no-literal-string */}
                            <h1 className="font-extrabold text-base text-slate-800 dark:text-slate-100 tracking-tight">Estimatix</h1>
                        </div>
                        
                        <div className="flex items-center gap-0.5">
                            <motion.button 
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={toggleTheme}
                                className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-400 transition-colors group"
                                title={theme === 'light' ? t('common.theme_dark', 'Темная тема') : t('common.theme_light', 'Светлая тема')}
                            >
                                {theme === 'light' ? <Moon size={16} className="group-hover:text-blue-600"/> : <Sun size={16} className="group-hover:text-amber-400"/>}
                            </motion.button>

                            <LanguageSelector />

                            <motion.button 
                                whileHover={{ scale: 1.1, rotate: 90 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={onClose}
                                className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-400 hover:text-red-500 transition-colors"
                                title={t('common.close', 'Закрыть')}
                            >
                                <X size={18} />
                            </motion.button>
                        </div>
                    </div>

                    {/* Tab bar */}
                    <div className="px-3 pt-2 pb-0 border-b border-slate-100 dark:border-slate-800">
                        <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl gap-0.5">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`relative flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-[11px] font-bold transition-all ${
                                        activeTab === tab.id
                                            ? 'text-blue-600 dark:text-blue-400'
                                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                                    }`}
                                >
                                    {activeTab === tab.id && (
                                        <motion.div
                                            layoutId="sidebar-active-tab"
                                            className="absolute inset-0 bg-white dark:bg-slate-700 rounded-lg shadow-sm"
                                            transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                                        />
                                    )}
                                    <tab.icon size={14} className="relative z-10" />
                                    <span className="relative z-10">{tab.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Tab content — scrollable */}
                    <div className="flex-1 overflow-y-auto custom-scrollbar">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeTab}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -6 }}
                                transition={{ duration: 0.15 }}
                            >
                                {activeTab === 'stats' && (
                                    <ProjectStats
                                        projectTotal={projectTotal}
                                        totalArea={totalArea}
                                        safetyMargin={safetyMargin}
                                        setSafetyMargin={setSafetyMargin}
                                        onDownloadExcel={onDownloadExcel}
                                        rooms={rooms}
                                        projectItems={projectItems}
                                        meta={meta}
                                        materialTotals={materialTotals}
                                        projectOverrides={projectOverrides}
                                    />
                                )}

                                {activeTab === 'project' && (
                                    <div className="p-4">
                                        <ProjectMetaForm 
                                            meta={meta}
                                            updateMeta={updateMeta}
                                        />
                                    </div>
                                )}

                                {activeTab === 'history' && (
                                    <div className="p-4">
                                        <VersionHistory 
                                            activeProjectId={activeProjectId}
                                            commitVersion={commitVersion}
                                            onLoadSnapshot={onLoadSnapshot}
                                        />
                                    </div>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* Footer — always visible */}
                    <div className="p-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30 space-y-1.5">
                        <motion.button 
                            whileHover={{ scale: 1.01, x: 3 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={onSyncPrices}
                            className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-white dark:hover:bg-slate-800 hover:shadow-sm transition-colors text-slate-500 hover:text-green-600"
                        >
                            <RefreshCcw size={18} />
                            <span className="font-bold text-xs">{t('editor.sidebar.update_prices', 'Обновить цены')}</span>
                        </motion.button>
                        
                        <div className="flex gap-1.5">
                            <Link to="/" className="flex-1">
                                <motion.div 
                                    whileHover={{ scale: 1.01 }} 
                                    whileTap={{ scale: 0.98 }}
                                    className="flex items-center justify-center gap-2 p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-700 hover:shadow-sm transition-colors"
                                >
                                    <Home size={16} />
                                    <span className="font-bold text-xs">{t('editor.sidebar.home', 'Главная')}</span>
                                </motion.div>
                            </Link>

                            <Link to="/dashboard" className="flex-1">
                                <motion.div 
                                    whileHover={{ scale: 1.01 }} 
                                    whileTap={{ scale: 0.98 }}
                                    className="flex items-center justify-center gap-2 p-2.5 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:shadow-sm transition-colors"
                                >
                                    <LayoutGrid size={16} />
                                    <span className="font-bold text-xs">{t('editor.sidebar.dashboard', 'Панель')}</span>
                                </motion.div>
                            </Link>
                        </div>
                    </div>
                </div>
            </motion.aside>
        </>
    );
};
